import { useEffect, useState } from "react";
import { Award, Loader2, Lock, Trophy } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Achievements/badges grid. Reads GET /api/achievements which returns
// { badge_id, name, description, icon (unicode/emoji-safe), category,
// requirement, progress, raw_progress, unlocked }.
export default function Achievements() {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/achievements").then(({ data }) => {
      if (cancelled) return;
      setItems(Array.isArray(data) ? data : (data?.items || data?.results || []));
    }).catch((e) => {
      if (!cancelled) setError(e?.response?.data?.detail || "Could not load achievements.");
    });
    return () => { cancelled = true; };
  }, [api]);

  if (error) return <div className="card-elev p-6" style={{ color: "var(--premium)" }}>{error}</div>;
  if (!items) return (
    <div className="max-w-3xl mx-auto py-12 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
    </div>
  );

  const unlockedCount = items.filter((b) => b.unlocked).length;
  const grouped = items.reduce((acc, b) => {
    const k = b.category || "Other";
    (acc[k] = acc[k] || []).push(b);
    return acc;
  }, {});
  const order = ["Milestones", "Explorer", "Adventure", "Terrain", "Style", "Leaderboard", "Other"];
  const cats = order.filter((c) => grouped[c]);

  return (
    <div className="flex flex-col gap-6" data-testid="achievements-page">
      <header>
        <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Achievements
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ color: "var(--on-surface)" }}>
          {unlockedCount} of {items.length} unlocked
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
          Complete adventures to earn badges. Locked badges show your current progress.
        </p>
      </header>

      {cats.map((cat) => (
        <section key={cat}>
          <h2 className="text-sm font-extrabold uppercase tracking-widest mb-3"
              style={{ color: "var(--brand)" }} data-testid={`achievements-cat-${cat.toLowerCase()}`}>
            {cat}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {grouped[cat].map((b) => <BadgeCard key={b.badge_id || b.id || b.name} b={b} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function BadgeCard({ b }) {
  const progressPct = Math.min(100, Math.round((b.progress / (b.requirement || 1)) * 100));
  return (
    <article
      className="card-elev p-4 flex gap-4 relative"
      style={{ opacity: b.unlocked ? 1 : 0.75 }}
      data-testid={`badge-${b.badge_id || b.name}`}
    >
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shrink-0"
           style={{
             background: b.unlocked
               ? "color-mix(in srgb, var(--premium) 20%, transparent)"
               : "color-mix(in srgb, var(--on-surface-muted) 10%, transparent)",
             color: b.unlocked ? "var(--premium)" : "var(--on-surface-muted)",
           }}>
        {b.icon ? <span>{b.icon}</span> : (b.unlocked ? <Trophy className="w-6 h-6" /> : <Lock className="w-6 h-6" />)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-extrabold text-base leading-tight truncate" style={{ color: "var(--on-surface)" }}>
            {b.name}
          </h3>
          {b.unlocked && (
            <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
              Unlocked
            </span>
          )}
        </div>
        {b.description && (
          <p className="text-xs mt-1" style={{ color: "var(--on-surface-muted)" }}>
            {b.description}
          </p>
        )}
        <div className="mt-3">
          <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest mb-1"
               style={{ color: "var(--on-surface-muted)" }}>
            <span>{b.progress}/{b.requirement}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="w-full h-1.5 rounded-full overflow-hidden"
               style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)" }}>
            <div className="h-full rounded-full transition-all"
                 style={{
                   width: `${progressPct}%`,
                   background: b.unlocked ? "var(--brand)" : "var(--on-surface-muted)",
                 }} />
          </div>
        </div>
      </div>
    </article>
  );
}
